import React from "react";
import { MLSDetailResponse } from "../../../api/mls";
import { PropertyCard } from "./PropertyCard";
import styles from "./PropertyDetail.module.css";
import { useTenant } from "../../../contexts/TenantContext";
import { formatPhone } from "../../../utils/formatPhone";
import { isPhoneNumber, getContactHref } from "../../../utils/contactUtils";

interface PropertyDetailsGridProps {
  propertyData: MLSDetailResponse["data"]["listing"];
  features: string[];
  formatPrice: (price: number) => string;
  primaryColor: string;
  teamMember?: any; // Add teamMember prop
}

export const PropertyDetailsGrid: React.FC<PropertyDetailsGridProps> = ({
  propertyData,
  features,
  formatPrice,
  primaryColor,
  teamMember,
}) => {
  const { config } = useTenant();
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-0 lg:gap-8 xl:gap-16 mt-10 xl:mt-12">
      {/* Left Column - Description and Details */}
      <div className="lg:col-span-2 pb-5 lg:pb-10">
        {/* Description */}
        <PropertyCard className="mb-6">
          <h2 className="text-sm uppercase tracking-wider text-gray-700 font-medium mb-4">
            Description
          </h2>
          <p className={styles.description}>{propertyData.publicRemarks}</p>
        </PropertyCard>

        {/* Property Details */}
        <PropertyCard>
          <h2 className="text-sm uppercase tracking-wider text-gray-700 font-medium mb-4">
            Property Details
          </h2>
          <div className={styles.propertySpecs}>
            {/* Property Details */}
            <div className={styles.specGroup}>
              <div className="grid grid-cols-2 gap-4">
                {propertyData.property?.yearBuilt ? (
                  <div className={styles.specItem}>
                    <span className={styles.specLabel}>Built In:</span>
                    <span className={styles.specValue}>
                      {propertyData.property.yearBuilt}
                    </span>
                  </div>
                ) : null}
                {propertyData.property?.lotSizeAcres ||
                propertyData.property?.lotSizeSquareFeet ? (
                  <div className={styles.specItem}>
                    <span className={styles.specLabel}>Lot Size:</span>
                    <span className={styles.specValue}>
                      {propertyData.property.lotSizeAcres
                        ? `${parseFloat(
                            propertyData.property.lotSizeAcres,
                          ).toLocaleString()} acres`
                        : `${propertyData.property.lotSizeSquareFeet.toLocaleString()} sqft`}
                    </span>
                  </div>
                ) : null}
                {propertyData.property?.garageSpaces ? (
                  <div className={styles.specItem}>
                    <span className={styles.specLabel}>Parking:</span>
                    <span className={styles.specValue}>
                      {propertyData.property.garageSpaces} spaces
                    </span>
                  </div>
                ) : null}
                {propertyData.property?.stories ? (
                  <div className={styles.specItem}>
                    <span className={styles.specLabel}>Stories:</span>
                    <span className={styles.specValue}>
                      {propertyData.property.stories}
                    </span>
                  </div>
                ) : null}
              </div>
            </div>

            {/* Location & Community */}
            {propertyData.property?.subdivisionName ||
            propertyData.address?.countyOrParish ? (
              <div className={styles.specGroup}>
                <h2 className="text-sm uppercase tracking-wider text-gray-700 font-medium mb-4">
                  Location & Community
                </h2>
                <div className="grid grid-cols-2 gap-4">
                  {propertyData.property?.subdivisionName ? (
                    <div className={styles.specItem}>
                      <span className={styles.specLabel}>Subdivision:</span>
                      <span className={styles.specValue}>
                        {propertyData.property.subdivisionName}
                      </span>
                    </div>
                  ) : null}
                  {propertyData.address?.countyOrParish ? (
                    <div className={styles.specItem}>
                      <span className={styles.specLabel}>County:</span>
                      <span className={styles.specValue}>
                        {propertyData.address.countyOrParish}
                      </span>
                    </div>
                  ) : null}
                </div>
              </div>
            ) : null}

            {/* Views */}
            {propertyData.property?.view ? (
              <div className={styles.specGroup}>
                <h2 className="text-sm uppercase tracking-wider text-gray-700 font-medium mb-4">
                  Views & Location Features
                </h2>
                <div className="grid grid-cols-2 gap-4">
                  <div className={styles.specItem}>
                    <span className={styles.specLabel}>View:</span>
                    <span className={styles.specValue}>
                      {propertyData.property.view}
                    </span>
                  </div>
                </div>
              </div>
            ) : null}

            {/* Property Type & Features */}
            <div className={styles.specGroup}>
              <h2 className="text-sm uppercase tracking-wider text-gray-700 font-medium mb-4">
                Property Type & Features
              </h2>
              <div className="grid grid-cols-2 gap-4">
                {propertyData.property?.propertySubType ? (
                  <div className={styles.specItem}>
                    <span className={styles.specLabel}>Property Type:</span>
                    <span className={styles.specValue}>
                      {propertyData.property.propertySubType}
                    </span>
                  </div>
                ) : null}
                {propertyData.property?.hasBasement !== null ? (
                  <div className={styles.specItem}>
                    <span className={styles.specLabel}>Basement:</span>
                    <span className={styles.specValue}>
                      {propertyData.property.hasBasement ? "Yes" : "No"}
                    </span>
                  </div>
                ) : null}
                {propertyData.property?.hasPool !== null ? (
                  <div className={styles.specItem}>
                    <span className={styles.specLabel}>Pool:</span>
                    <span className={styles.specValue}>
                      {propertyData.property.hasPool ? "Yes" : "No"}
                    </span>
                  </div>
                ) : null}
                {propertyData.property?.isNewConstruction !== undefined ? (
                  <div className={styles.specItem}>
                    <span className={styles.specLabel}>New Construction:</span>
                    <span className={styles.specValue}>
                      {propertyData.property.isNewConstruction ? "Yes" : "No"}
                    </span>
                  </div>
                ) : null}
                {propertyData.property?.isRental !== undefined ? (
                  <div className={styles.specItem}>
                    <span className={styles.specLabel}>Rental Property:</span>
                    <span className={styles.specValue}>
                      {propertyData.property.isRental ? "Yes" : "No"}
                    </span>
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </PropertyCard>

        {/* Schools */}
        {propertyData.schools ? (
          <PropertyCard className="my-6">
            <h2 className="text-sm uppercase tracking-wider text-gray-700 font-medium mb-4 pt-5">
              Nearby Schools
            </h2>
            <div className={styles.specGroup}>
              <div className="mb-4">
                <div className={styles.specLabel}>Elementary School:</div>
                <div className={`${styles.specValue} mt-2`}>
                  {propertyData.schools.elementarySchool || "-"}
                </div>
              </div>
              <div className="mb-4">
                <div className={styles.specLabel}>Middle/Junior School:</div>
                <div className={`${styles.specValue} mt-2`}>
                  {propertyData.schools.middleOrJuniorSchool || "-"}
                </div>
              </div>
              <div>
                <div className={styles.specLabel}>High School:</div>
                <div className={`${styles.specValue} mt-2`}>
                  {propertyData.schools.highSchool || "-"}
                </div>
              </div>
            </div>
          </PropertyCard>
        ) : null}
      </div>

      {/* Right Column */}
      <div className="lg:col-span-1">
        <div className="sticky top-24">
          <PropertyCard className="border-2 border-gray-300 shadow-xl overflow-hidden">
            {/* Contact Card Header */}
            <div className="bg-gradient-to-r from-gray-50 via-gray-100 to-gray-50 px-4 xl:px-6 py-4 border-b border-gray-200">
              <h2 className="text-base uppercase text-gray-800 font-semibold mb-1">
                Have Questions?
              </h2>
              <p className="text-sm text-gray-600">
                Get in touch to schedule a tour or learn more
              </p>
            </div>

            <div className="flex flex-col p-4 xl:p-6">
              <div className="flex items-start justify-between mb-6">
                <div>
                  {/* If team member is provided, show team member info */}
                  {teamMember ? (
                    <>
                      <h3 className="text-[13px] md:text-base font-normal text-gray-900">
                        {config?.orgData?.brokerage_name ||
                          config?.orgData?.org_name ||
                          ""}
                      </h3>
                      <p className="text-[13px] md:text-base text-gray-600 mb-4">
                        {`${teamMember.first_name} ${teamMember.last_name}`}
                      </p>
                      {teamMember.phone_number && (
                        <a
                          href={`tel:${teamMember.phone_number}`}
                          className="text-[13px] md:text-base text-gray-900 font-normal underline mb-1 hover:text-primary transition-colors block"
                        >
                          {formatPhone(teamMember.phone_number)}
                        </a>
                      )}
                      {teamMember.email && (
                        <a
                          href={`mailto:${teamMember.email}`}
                          className="text-[13px] md:text-base text-gray-900 font-normal underline hover:text-primary transition-colors block"
                        >
                          {teamMember.email}
                        </a>
                      )}
                    </>
                  ) : config?.orgData?.primary_contact &&
                    config?.orgData?.primary_contact?.useForPropertyPage ? (
                    <>
                      <h3 className="text-[13px] md:text-base font-normal text-gray-900">
                        {config.orgData.brokerage_name || ""}
                      </h3>
                      <p className="text-[13px] md:text-base text-gray-600 mb-4">
                        {config.orgData.primary_contact.name}
                      </p>
                      {config.orgData.primary_contact.phone && (
                        <a
                          href={`tel:${config.orgData.primary_contact.phone}`}
                          className="text-[13px] md:text-base text-gray-900 font-normal underline mb-1 hover:text-primary transition-colors block"
                        >
                          {formatPhone(config.orgData.primary_contact.phone)}
                        </a>
                      )}
                      {config.orgData.primary_contact.email && (
                        <a
                          href={`mailto:${config.orgData.primary_contact.email}`}
                          className="text-[13px] md:text-base text-gray-900 font-normal underline hover:text-primary transition-colors block"
                        >
                          {config.orgData.primary_contact.email}
                        </a>
                      )}
                    </>
                  ) : (
                    <>
                      <h3 className="text-[13px] md:text-base font-normal mb-2 text-gray-900">
                        {config?.name ||
                          config?.orgData?.org_name ||
                          "Schedule a Tour"}
                      </h3>
                      {config?.orgData?.phoneNumbers?.inbound ? (
                        <a
                          href={`tel:${config.orgData.phoneNumbers.inbound}`}
                          className="text-[13px] md:text-base text-gray-900 font-normal underline mb-1 hover:text-primary transition-colors block"
                        >
                          {formatPhone(config.orgData.phoneNumbers.inbound)}
                        </a>
                      ) : null}
                      {config?.orgData?.email ? (
                        <a
                          href={`mailto:${config.orgData.email}`}
                          className="text-[13px] md:text-base text-gray-900 font-normal underline hover:text-primary transition-colors block"
                        >
                          {config.orgData.email}
                        </a>
                      ) : null}
                    </>
                  )}
                </div>
              </div>
              <div className="flex flex-col gap-3">
                {(() => {
                  // Determine tour link - use branding tour link if set, otherwise fall back to phone
                  const tourLink = config?.branding?.propertyDetails?.tour;
                  const fallbackPhone =
                    teamMember?.phone_number ||
                    (config?.orgData?.primary_contact?.useForPropertyPage
                      ? config?.orgData?.primary_contact?.phone
                      : undefined) ||
                    config?.orgData?.phoneNumbers?.inbound;

                  const tourHref = tourLink
                    ? getContactHref(tourLink)
                    : fallbackPhone
                      ? `tel:${fallbackPhone}`
                      : undefined;

                  const tourIsExternal = tourLink && !isPhoneNumber(tourLink);

                  return tourHref ? (
                    <a
                      href={tourHref}
                      {...(tourIsExternal
                        ? { target: "_blank", rel: "noopener noreferrer" }
                        : {})}
                      className="w-full text-white py-3 text-xs xl:text-sm rounded-lg hover:opacity-90 transition-colors text-center"
                      style={{ backgroundColor: primaryColor }}
                    >
                      Schedule a Tour
                    </a>
                  ) : null;
                })()}
                {(() => {
                  // Determine contact link - use branding contact link if set, otherwise fall back to phone
                  const contactLink =
                    config?.branding?.propertyDetails?.contact;
                  const fallbackPhone =
                    teamMember?.phone_number ||
                    (config?.orgData?.primary_contact?.useForPropertyPage
                      ? config?.orgData?.primary_contact?.phone
                      : undefined) ||
                    config?.orgData?.phoneNumbers?.inbound;

                  const contactHref = contactLink
                    ? getContactHref(contactLink)
                    : fallbackPhone
                      ? `tel:${fallbackPhone}`
                      : undefined;

                  const contactIsExternal =
                    contactLink && !isPhoneNumber(contactLink);

                  return contactHref ? (
                    <a
                      href={contactHref}
                      {...(contactIsExternal
                        ? { target: "_blank", rel: "noopener noreferrer" }
                        : {})}
                      className="w-full bg-white py-3 text-xs xl:text-sm rounded-lg hover:bg-gray-50 transition-colors border text-center"
                      style={{ color: primaryColor, borderColor: primaryColor }}
                    >
                      Contact Agent
                    </a>
                  ) : null;
                })()}
              </div>
            </div>
          </PropertyCard>
        </div>
      </div>
    </div>
  );
};

export default PropertyDetailsGrid;
